import React from 'react';
import { Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';

export const LaunchpadGuidePage: React.FC = () => {
  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-8 pb-12 max-w-3xl mx-auto px-4">
        {/* Page Header */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-1 font-heading-1 text-default-font">
            Creator Guide
          </span>
          <p className="text-body font-body text-subtext-color">
            Everything you need to know about launching your NFT collection on XRP.Deals
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Introduction */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            What is the Launchpad?
          </span>
          <p className="text-body font-body text-subtext-color">
            The XRP.Deals Launchpad is a full-service minting platform for NFT creators on the XRP Ledger. We handle all the technical complexity so you can focus on your art and community.
          </p>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Why launch on XRP.Deals?
                </span>
                <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
                  <li>No upfront gas fees — we fund minting transactions</li>
                  <li>Automatic metadata pinning on IPFS</li>
                  <li>Built-in allowlist support for your community</li>
                  <li>Real-time sales tracking and revenue dashboard</li>
                  <li>Secondary market royalties configured automatically</li>
                  <li>Your collection appears on XRP.Deals marketplace instantly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Before You Start */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Before You Start
          </span>
          <p className="text-body font-body text-subtext-color">
            Make sure you have the following ready before creating your collection:
          </p>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  XRPL Wallet
                </span>
                <p className="text-body font-body text-subtext-color">
                  You need an XRPL wallet connected to XRP.Deals. This wallet will be the issuer of your NFTs and receive all minting revenue.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Collection Assets
                </span>
                <p className="text-body font-body text-subtext-color">
                  Your NFT images and metadata files organized in a folder with <code className="bg-neutral-200 px-1 rounded">images/</code> and <code className="bg-neutral-200 px-1 rounded">metadata/</code> subdirectories. We accept PNG, JPG, WEBP, and GIF image formats.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Taxon Number
                </span>
                <p className="text-body font-body text-subtext-color">
                  A unique number that identifies your collection on-chain. Choose any number between 0 and 4,294,967,295 that you haven't used before for other collections.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Step 1: Create Your Collection */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Step 1: Create Your Collection
          </span>
          <p className="text-body font-body text-subtext-color">
            Start by providing basic information about your collection:
          </p>

          <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
            <li><strong>Collection Name</strong> — The display name for your collection</li>
            <li><strong>URL Slug</strong> — A unique URL-friendly identifier (e.g., "cool-cats" for xrp.deals/drops/cool-cats)</li>
            <li><strong>Description</strong> — Tell collectors what makes your collection special</li>
            <li><strong>Taxon</strong> — Your unique collection identifier on the XRPL</li>
            <li><strong>Social Links</strong> — Optional links to your website, Twitter, and Discord</li>
          </ul>

          <p className="text-body font-body text-subtext-color">
            After filling in the basics, click "Create Draft" to save your collection and continue to the next steps.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Step 2: Upload Your Assets */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Step 2: Upload Your Assets
          </span>
          <p className="text-body font-body text-subtext-color">
            Upload a folder containing your NFT images and metadata. The folder must have a specific structure with two subdirectories.
          </p>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              Required Folder Structure
            </span>
            <pre className="bg-neutral-100 rounded-lg p-4 overflow-x-auto text-caption font-mono text-default-font">
{`my-collection/
├── images/
│   ├── 1.png
│   ├── 2.png
│   ├── 3.png
│   └── ...
└── metadata/
    ├── 1.json
    ├── 2.json
    ├── 3.json
    └── ...`}
            </pre>
            <p className="text-caption font-caption text-subtext-color mt-3">
              <strong>Important:</strong> The folder must contain exactly two subdirectories named <code className="bg-neutral-200 px-1 rounded">images</code> and <code className="bg-neutral-200 px-1 rounded">metadata</code>. Files are matched by number (1.png with 1.json, 2.png with 2.json, etc.).
            </p>
          </div>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              File Naming
            </span>
            <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
              <li>Use sequential numbers starting from 1 (1, 2, 3, ...)</li>
              <li>Image and metadata files must have matching numbers</li>
              <li>Image extensions: <code className="bg-neutral-200 px-1 rounded">.png</code>, <code className="bg-neutral-200 px-1 rounded">.jpg</code>, <code className="bg-neutral-200 px-1 rounded">.jpeg</code>, <code className="bg-neutral-200 px-1 rounded">.webp</code>, or <code className="bg-neutral-200 px-1 rounded">.gif</code></li>
              <li>Metadata extension: <code className="bg-neutral-200 px-1 rounded">.json</code></li>
            </ul>
          </div>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              Metadata JSON Format
            </span>
            <pre className="bg-neutral-100 rounded-lg p-4 overflow-x-auto text-caption font-mono text-default-font">
{`{
  "name": "Cool Cat #1",
  "description": "A very cool cat from the Cool Cats collection.",
  "attributes": [
    { "trait_type": "Background", "value": "Blue" },
    { "trait_type": "Fur", "value": "Orange" },
    { "trait_type": "Eyes", "value": "Sunglasses" }
  ]
}`}
            </pre>
            <p className="text-caption font-caption text-subtext-color mt-3">
              The <code className="bg-neutral-200 px-1 rounded">name</code> and <code className="bg-neutral-200 px-1 rounded">description</code> fields are required. <code className="bg-neutral-200 px-1 rounded">attributes</code> is optional but recommended for trait-based collections.
            </p>
          </div>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              Image Requirements
            </span>
            <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
              <li>Formats: PNG, JPG, WEBP, or GIF</li>
              <li>Recommended: Square images (1:1 aspect ratio) for best display</li>
              <li>Maximum file size: 10MB per image</li>
              <li>Recommended resolution: 1000x1000 to 2000x2000 pixels</li>
            </ul>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Step 3: Configure Settings */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Step 3: Configure Settings
          </span>
          <p className="text-body font-body text-subtext-color">
            Set up pricing, royalties, and minting rules for your collection:
          </p>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Mint Price
                </span>
                <p className="text-body font-body text-subtext-color">
                  The price collectors pay to mint each NFT, in XRP. You can set different prices for each phase: Allowlist Phase 1, Allowlist Phase 2, and Public. Set a price to 0 for free mints in that phase.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Royalties (Transfer Fee)
                </span>
                <p className="text-body font-body text-subtext-color">
                  The percentage you receive from secondary sales (0-50%). This is built into the NFT on-chain via the XRPL's transfer fee.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Mint Limits
                </span>
                <p className="text-body font-body text-subtext-color">
                  Maximum NFTs per wallet. Each allowlist phase has independent per-wallet limits, plus a separate limit for public mint.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Schedule
                </span>
                <p className="text-body font-body text-subtext-color">
                  Set start and end times for your mint. Leave blank for manual control, or schedule automatic start/end times.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Step 4: Allowlist */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Step 4: Allowlist (Optional)
          </span>
          <p className="text-body font-body text-subtext-color">
            Give your community early or exclusive access with up to two allowlist phases before public mint.
          </p>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              Multi-Phase Allowlist Support
            </span>
            <p className="text-body font-body text-subtext-color mb-3">
              You can configure up to two separate allowlist phases, each with its own:
            </p>
            <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
              <li><strong>Separate wallet lists</strong> — Different addresses for Phase 1 and Phase 2</li>
              <li><strong>Different pricing</strong> — Set unique mint prices per phase</li>
              <li><strong>Per-wallet limits</strong> — Custom limits for each phase</li>
              <li><strong>Scheduled start times</strong> — Phases transition automatically</li>
            </ul>
            <p className="text-caption font-caption text-subtext-color mt-3">
              Wallets can appear on both allowlists with different limits — they'll be able to mint during both phases.
            </p>
          </div>

          <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
            <li>Add addresses manually or import via CSV for each phase</li>
            <li>Skip phases you don't need (e.g., only Phase 2 + Public, or straight to Public)</li>
            <li>Phases progress forward only: Phase 1 → Phase 2 → Public</li>
          </ul>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              CSV Import Format
            </span>
            <pre className="bg-neutral-100 dark:bg-neutral-800 rounded-lg p-4 overflow-x-auto text-caption font-mono text-neutral-700 dark:text-neutral-200">
{`wallet_address,mint_limit
rHb9CJAWyB4rj91VRWn96DkukG4bwdtyTh,5
rPT1Sjq2YGrBMTttX4GZHjKu9dyfzbpAYe,3
rN7n3473SaZBCG4dFL83w7a1RXtXtbk2D9,`}
            </pre>
            <p className="text-caption font-caption text-subtext-color mt-3">
              The mint_limit column is optional. Leave it blank to use the default allowlist limit.
            </p>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Step 5: Review & Submit */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Step 5: Review & Submit
          </span>
          <p className="text-body font-body text-subtext-color">
            Before submitting, use the Preview feature to see how your collection will appear to collectors. Make sure everything looks right!
          </p>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <span className="text-body-bold font-body-bold text-default-font block mb-3">
              Pre-Launch Checklist
            </span>
            <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
              <li>All NFT assets uploaded and validated</li>
              <li>Collection name, description, and images look correct</li>
              <li>Pricing is set correctly for each phase (Phase 1, Phase 2, Public)</li>
              <li>Royalty percentage is what you want</li>
              <li>Allowlist addresses are correct for each enabled phase</li>
              <li>Phase start times are scheduled in the correct order</li>
            </ul>
          </div>

          <p className="text-body font-body text-subtext-color">
            When you're ready, click "Submit for Approval" to send your collection to our team for review.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* After Submission */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            After Submission
          </span>
          <p className="text-body font-body text-subtext-color">
            Here's what happens after you submit your collection:
          </p>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  1. Admin Review
                </span>
                <p className="text-body font-body text-subtext-color">
                  Our team reviews your collection to ensure everything is in order. This usually takes less than 24 hours.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  2. MintAuth Authorization
                </span>
                <p className="text-body font-body text-subtext-color">
                  Once approved, you'll need to sign a transaction authorizing XRP.Deals to mint NFTs on your behalf. This is an XRPL feature that lets us handle minting without accessing your wallet.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  3. Launch Fee Payment
                </span>
                <p className="text-body font-body text-subtext-color">
                  Pay the one-time launch fee to activate your collection. The fee covers IPFS pinning and platform services.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Once You're Live */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Once You're Live
          </span>
          <p className="text-body font-body text-subtext-color">
            After activation, your collection goes live and collectors can start minting!
          </p>

          <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
            <li><strong>Allowlist Phase 1</strong> — Only Phase 1 allowlisted addresses can mint (if configured)</li>
            <li><strong>Allowlist Phase 2</strong> — Only Phase 2 allowlisted addresses can mint (if configured)</li>
            <li><strong>Public Phase</strong> — Anyone can mint at the public price</li>
            <li><strong>Pause/Resume</strong> — You can pause minting at any time if needed</li>
            <li><strong>Monitor Sales</strong> — Track mints in real-time from your dashboard</li>
          </ul>

          <p className="text-body font-body text-subtext-color">
            Phases transition automatically at their scheduled times. You can also manually advance phases from your dashboard.
          </p>

          <p className="text-body font-body text-subtext-color">
            Share your mint page link with your community: <span className="text-brand-600">xrp.deals/drops/your-slug</span>
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Getting Paid */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Getting Paid
          </span>
          <p className="text-body font-body text-subtext-color">
            All revenue from minting goes directly to your connected wallet. Here's how payments work:
          </p>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Mint Revenue
                </span>
                <p className="text-body font-body text-subtext-color">
                  When collectors mint, their payment is held in escrow. Settlements are processed regularly and XRP is sent directly to your wallet minus the platform fee.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Revenue Dashboard
                </span>
                <p className="text-body font-body text-subtext-color">
                  Track all your earnings from the Revenue tab in your collection dashboard. See pending, processing, and completed settlements.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Secondary Sales Royalties
                </span>
                <p className="text-body font-body text-subtext-color">
                  Royalties from secondary sales are paid automatically on-chain via the XRPL's transfer fee mechanism. You'll see these in the Royalties tab.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* FAQ */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Frequently Asked Questions
          </span>

          <div className="w-full space-y-4">
            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                How much does it cost to launch?
              </span>
              <p className="text-body font-body text-subtext-color">
                There's a one-time launch fee to cover IPFS pinning and platform services. The exact amount depends on your collection size and is shown during the approval process.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                What's a taxon and how do I choose one?
              </span>
              <p className="text-body font-body text-subtext-color">
                A taxon is a number that groups your NFTs together on the XRPL. Pick any number you haven't used before. Many creators use sequential numbers (1, 2, 3...) for each of their collections.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                Can I edit my collection after submitting?
              </span>
              <p className="text-body font-body text-subtext-color">
                You can edit your collection while it's in draft status. Once submitted for approval, most settings are locked. If approved, you can still update some things like the schedule and allowlist.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                What's MintAuth and why do I need it?
              </span>
              <p className="text-body font-body text-subtext-color">
                MintAuth is an XRPL feature that lets you authorize another account to mint NFTs on your behalf. This allows XRP.Deals to mint for collectors without ever accessing your wallet's secret keys. It's secure and you can revoke it anytime.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                How long does review take?
              </span>
              <p className="text-body font-body text-subtext-color">
                Most collections are reviewed within 24 hours. We check that your assets are properly formatted and the collection meets our guidelines.
              </p>
            </div>

            <div className="rounded-lg border border-neutral-border bg-default-background p-6">
              <span className="text-body-bold font-body-bold text-default-font block mb-2">
                Can I launch without an allowlist?
              </span>
              <p className="text-body font-body text-subtext-color">
                Absolutely! Allowlists are optional. You can use both phases, just one phase, or skip them entirely and go straight to public mint. Common configurations: Phase 1 + Public, Phase 2 only + Public, or Public only.
              </p>
            </div>
          </div>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Ready to Launch CTA */}
        <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6 text-center">
          <span className="text-heading-3 font-heading-3 text-default-font block mb-2">
            Ready to Launch?
          </span>
          <p className="text-body font-body text-subtext-color mb-4">
            Start creating your collection now. We're here to help if you have questions.
          </p>
          <Link
            to="/launchpad/new"
            className="inline-flex items-center justify-center px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-body-bold font-body-bold transition-colors"
          >
            Create Your Collection
          </Link>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
